import { useState } from 'react'
import { CountryPicker, FormDialog } from '@/components/common'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { formProblem } from '@/lib/api/fieldErrors'
import { useEmployeeMutations } from '../api'
import type { EmployeeDetail } from '../types'
import { ChoiceField, Field, TextField } from './EmployeeFields'
import { EMPLOYMENT_TYPES, GENDERS } from './employeeFieldOptions'

const MARITAL_STATUSES = [
  { value: 'SINGLE', label: 'Single' },
  { value: 'MARRIED', label: 'Married' },
  { value: 'DIVORCED', label: 'Divorced' },
  { value: 'WIDOWED', label: 'Widowed' },
  { value: 'UNDISCLOSED', label: 'Prefer not to say' },
] as const

/**
 * Editing the person and the employment.
 *
 * <p>Deliberately not the department, grade or manager. Those live on the assignment, which is
 * only true as of a date: changing them here would silently rewrite where somebody was last
 * month as well as where they are now, and every attendance and payroll figure that read the
 * old placement would stop reconciling. The Placement tab records a dated move instead, and
 * the server refuses org changes sent through this endpoint for the same reason.
 */
export function EditEmployeeDialog({
  employee, open, onOpenChange,
}: {
  employee: EmployeeDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { update } = useEmployeeMutations()
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const [draft, setDraft] = useState({
    firstName: employee.firstName,
    middleName: employee.middleName ?? '',
    lastName: employee.lastName ?? '',
    workEmail: employee.summary.workEmail ?? '',
    workPhone: employee.summary.workPhone ?? '',
    gender: employee.gender ?? '',
    dateOfBirth: employee.dateOfBirth ?? '',
    nationality: employee.nationality ?? '',
    maritalStatus: employee.maritalStatus ?? '',
    personalEmail: employee.personalEmail ?? '',
    personalPhone: employee.personalPhone ?? '',
    employmentType: employee.summary.employmentType ?? '',
    contractType: employee.contractType ?? '',
    contractEndDate: employee.contractEndDate ?? '',
    probationEndDate: employee.probationEndDate ?? '',
    confirmationDate: employee.confirmationDate ?? '',
    noticePeriodDays: employee.noticePeriodDays?.toString() ?? '',
  })

  function set<K extends keyof typeof draft>(key: K, value: string) {
    setDraft((previous) => ({ ...previous, [key]: value }))
    // A field somebody has changed is no longer the one the server complained about.
    setFieldErrors((previous) => {
      if (!(key in previous)) return previous
      const next = { ...previous }
      delete next[key]
      return next
    })
  }

  async function submit() {
    setError(null)
    setFieldErrors({})
    try {
      await update.mutateAsync({
        id: employee.summary.id,
        ...draft,
        // Blanks are sent as null rather than "": the server treats an empty string as a
        // value somebody typed, and "" is not a date.
        ...Object.fromEntries(
          Object.entries(draft).filter(([, value]) => value === '').map(([key]) => [key, null]),
        ),
        noticePeriodDays: draft.noticePeriodDays === '' ? null : Number(draft.noticePeriodDays),
      })
      onOpenChange(false)
    } catch (caught) {
      const problem = formProblem(caught, 'That could not be saved.')
      setFieldErrors(problem.fields)
      setError(problem.summary)
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit employee"
      description="Personal details and the terms of the employment. Department, grade and manager are changed on the Placement tab, as a dated move."
      size="lg"
      submitLabel="Save changes"
      loading={update.isPending}
      error={error}
      onSubmit={() => { void submit() }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField id="edit-first-name" label="First name" required error={fieldErrors.firstName}
          value={draft.firstName} onChange={(value) => set('firstName', value)} />
        <TextField id="edit-middle-name" label="Middle name" error={fieldErrors.middleName}
          value={draft.middleName} onChange={(value) => set('middleName', value)} />
        <TextField id="edit-last-name" label="Last name" error={fieldErrors.lastName}
          value={draft.lastName} onChange={(value) => set('lastName', value)} />
        <TextField id="edit-work-email" label="Work email" type="email" error={fieldErrors.workEmail}
          value={draft.workEmail} onChange={(value) => set('workEmail', value)} />
        <TextField id="edit-work-phone" label="Work phone" error={fieldErrors.workPhone}
          value={draft.workPhone} onChange={(value) => set('workPhone', value)} />
        <ChoiceField id="edit-gender" label="Gender" choices={GENDERS} error={fieldErrors.gender}
          value={draft.gender} onChange={(value) => set('gender', value)} />
        <Field id="edit-dob" label="Date of birth" error={fieldErrors.dateOfBirth}>
          <DatePicker id="edit-dob" value={draft.dateOfBirth}
            onChange={(event) => set('dateOfBirth', event.target.value)} />
        </Field>
        <Field id="edit-nationality" label="Nationality" error={fieldErrors.nationality}>
          <CountryPicker id="edit-nationality" value={draft.nationality}
            invalid={Boolean(fieldErrors.nationality)}
            onChange={(value) => set('nationality', value)} />
        </Field>
        <ChoiceField id="edit-marital" label="Marital status" choices={MARITAL_STATUSES} error={fieldErrors.maritalStatus}
          value={draft.maritalStatus} onChange={(value) => set('maritalStatus', value)} />
        <TextField id="edit-personal-email" label="Personal email" type="email" error={fieldErrors.personalEmail}
          value={draft.personalEmail} onChange={(value) => set('personalEmail', value)} />
        <TextField id="edit-personal-phone" label="Personal phone" error={fieldErrors.personalPhone}
          value={draft.personalPhone} onChange={(value) => set('personalPhone', value)} />
        <ChoiceField id="edit-employment-type" label="Employment type" choices={EMPLOYMENT_TYPES} error={fieldErrors.employmentType}
          value={draft.employmentType} onChange={(value) => set('employmentType', value)} />
        <TextField id="edit-contract-type" label="Contract type" error={fieldErrors.contractType}
          value={draft.contractType} onChange={(value) => set('contractType', value)} />
        <Field id="edit-contract-end" label="Contract end date" error={fieldErrors.contractEndDate}>
          <DatePicker id="edit-contract-end" value={draft.contractEndDate}
            onChange={(event) => set('contractEndDate', event.target.value)} />
        </Field>
        <Field id="edit-probation-end" label="Probation ends" error={fieldErrors.probationEndDate}>
          <DatePicker id="edit-probation-end" value={draft.probationEndDate}
            onChange={(event) => set('probationEndDate', event.target.value)} />
        </Field>
        <Field id="edit-confirmation" label="Confirmed on" error={fieldErrors.confirmationDate}>
          <DatePicker id="edit-confirmation" value={draft.confirmationDate}
            onChange={(event) => set('confirmationDate', event.target.value)} />
        </Field>
        <Field id="edit-notice" label="Notice period (days)" error={fieldErrors.noticePeriodDays}>
          <Input id="edit-notice" type="number" min={0} value={draft.noticePeriodDays}
            onChange={(event) => set('noticePeriodDays', event.target.value)} />
        </Field>
      </div>
    </FormDialog>
  )
}
